import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Helmet } from 'react-helmet';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: 'General',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const inquiryTypes = [
    { value: 'General', label: 'General Inquiry' },
    { value: 'Vendor Support', label: 'Vendor Support' },
    { value: 'Technical Issue', label: 'Technical Issue' },
    { value: 'Partnership', label: 'Partnership Opportunity' },
    { value: 'Billing', label: 'Billing & Payments' },
    { value: 'Feedback', label: 'Feedback & Suggestions' }
  ];

  const quickContactCards = [
    {
      title: 'Customer Support',
      description: 'General inquiries and assistance',
      contact: '(555) 123-4567',
      email: 'support@shaadinest.com',
      hours: 'Mon-Fri: 9am-6pm PST',
      icon: 'Headphones'
    },
    {
      title: 'Vendor Support',
      description: 'Vendor registration and account help',
      contact: '(555) 123-4568',
      email: 'vendors@shaadinest.com',
      hours: 'Mon-Fri: 8am-8pm PST',
      icon: 'Users'
    },
    {
      title: 'Technical Issues',
      description: 'Website and app technical support',
      contact: '(555) 123-4569',
      email: 'tech@shaadinest.com',
      hours: '24/7 Support Available',
      icon: 'Settings'
    },
    {
      title: 'Partnership',
      description: 'Business partnerships and collaborations',
      contact: '(555) 123-4570',
      email: 'partnerships@shaadinest.com',
      hours: 'Mon-Fri: 10am-5pm PST',
      icon: 'Handshake'
    }
  ];

  const officeLocations = [
    {
      city: 'San Francisco',
      address: '123 Market Street, Suite 400',
      state: 'CA 94103',
      phone: '(555) 123-4567',
      isHeadquarters: true
    },
    {
      city: 'New York',
      address: '456 Fifth Avenue, Floor 12',
      state: 'NY 10018',
      phone: '(555) 234-5678',
      isHeadquarters: false
    },
    {
      city: 'Chicago',
      address: '789 Michigan Avenue, Suite 200',
      state: 'IL 60611',
      phone: '(555) 345-6789',
      isHeadquarters: false
    },
    {
      city: 'Atlanta',
      address: '321 Peachtree Street, Suite 500',
      state: 'GA 30309',
      phone: '(555) 456-7890',
      isHeadquarters: false
    }
  ];

  const faqItems = [
    {
      question: 'How do I register as a vendor?',
      answer: 'You can register as a vendor by clicking the "Join as Vendor" button in the header and completing our comprehensive registration process. This includes business verification, portfolio submission, and background checks.'
    },
    {
      question: 'What are the booking fees?',
      answer: 'ShaadiNest charges a small service fee on successful bookings. The fee varies by service category and is transparently displayed during the booking process. There are no hidden charges.'
    },
    {
      question: 'How do I cancel or modify a booking?',
      answer: 'You can cancel or modify bookings through your dashboard up to 48 hours before the event. For same-day changes, please contact our support team directly for assistance.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, debit cards, PayPal, and bank transfers. All payments are processed securely through our encrypted payment system.'
    },
    {
      question: 'How do you verify vendor quality?',
      answer: 'All vendors undergo a rigorous verification process including background checks, portfolio reviews, reference validation, and ongoing performance monitoring based on customer feedback.'
    },
    {
      question: 'Do you offer customer support in multiple languages?',
      answer: 'Yes, we provide support in English, Hindi, Gujarati, Punjabi, Tamil, and Telugu to better serve our diverse South Asian community.'
    }
  ];

  const socialChannels = [
    {
      platform: 'Facebook',
      handle: '@ShaadiNest',
      url: '#',
      icon: 'Facebook'
    },
    {
      platform: 'Instagram',
      handle: '@ShaadiNest',
      url: '#',
      icon: 'Instagram'
    },
    {
      platform: 'Twitter',
      handle: '@ShaadiNest',
      url: '#',
      icon: 'Twitter'
    },
    {
      platform: 'WhatsApp',
      handle: '+1 (555) 123-4567',
      url: '#',
      icon: 'MessageCircle'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Show success message
      alert('Thank you for your message! We will get back to you within 24 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        inquiryType: 'General',
        message: ''
      });
    } catch (error) {
      alert('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const getResponseTime = (inquiryType) => {
    const responseTimes = {
      'General': '24 hours',
      'Vendor Support': '4-6 hours',
      'Technical Issue': '2-4 hours',
      'Partnership': '48 hours',
      'Billing': '4-6 hours',
      'Feedback': '24-48 hours'
    };
    return responseTimes[inquiryType] || '24 hours';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Us - ShaadiNest | Get Help & Support</title>
        <meta 
          name="description" 
          content="Get in touch with ShaadiNest for support, partnerships, or inquiries. Multiple contact options with 24/7 support for urgent issues." 
        />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                Get in Touch
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                We're here to help you create the perfect wedding experience. Reach out to us through any of our communication channels for prompt assistance.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  iconName="MessageCircle"
                  onClick={() => window.scrollTo({ top: 600, behavior: 'smooth' })}
                >
                  Send Message
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Phone"
                  onClick={() => window.open('tel:+15551234567')}
                >
                  Call Now
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Contact Cards */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Quick Contact
              </h2>
              <p className="text-lg text-text-secondary">
                Choose the best way to reach us based on your needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickContactCards.map((card, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6 hover:shadow-medium transition-shadow">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name={card.icon} size={24} className="text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                      {card.title}
                    </h3>
                    <p className="text-text-secondary mb-4">{card.description}</p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Phone" size={16} className="text-primary" />
                        <span className="text-text-primary font-medium">{card.contact}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Mail" size={16} className="text-primary" />
                        <span className="text-text-secondary text-sm">{card.email}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <Icon name="Clock" size={16} className="text-primary" />
                        <span className="text-text-secondary text-sm">{card.hours}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Information */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-xl shadow-soft p-8">
                <h2 className="font-heading font-bold text-2xl text-text-primary mb-6">
                  Send us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Full Name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                    <Input
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Inquiry Type
                    </label>
                    <select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                    >
                      {inquiryTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      required
                      className="w-full px-4 py-3 border border-border-light rounded-lg focus:outline-none focus:border-primary resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-text-secondary">
                        {formData.message.length}/1000 characters
                      </span>
                      <span className="text-sm text-text-secondary">
                        Expected response: {getResponseTime(formData.inquiryType)}
                      </span>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    iconName="Send"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Office Locations */}
                <div className="bg-white rounded-xl shadow-soft p-8">
                  <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
                    Our Offices
                  </h3>
                  
                  <div className="space-y-6">
                    {officeLocations.map((office, index) => (
                      <div key={index} className="border-b border-border-light pb-4 last:border-b-0 last:pb-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-heading font-medium text-lg text-text-primary">
                              {office.city}
                              {office.isHeadquarters && (
                                <span className="ml-2 px-2 py-1 bg-primary text-white text-xs rounded-full">
                                  HQ
                                </span>
                              )}
                            </h4>
                            <p className="text-text-secondary mt-1">
                              {office.address}
                            </p>
                            <p className="text-text-secondary">{office.state}</p>
                            <p className="text-primary font-medium mt-2">{office.phone}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="MapPin"
                            onClick={() => window.open(`https://maps.google.com?q=${office.address}, ${office.state}`)}
                          >
                            View Map
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-white rounded-xl shadow-soft p-8">
                  <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
                    Follow Us
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {socialChannels.map((channel, index) => (
                      <a
                        key={index}
                        href={channel.url}
                        className="flex items-center space-x-3 p-3 border border-border-light rounded-lg hover:border-primary hover:bg-primary-50 transition-colors"
                      >
                        <Icon name={channel.icon} size={20} className="text-primary" />
                        <div>
                          <p className="font-medium text-text-primary">{channel.platform}</p>
                          <p className="text-sm text-text-secondary">{channel.handle}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-text-secondary">
                Find quick answers to common questions about our platform
              </p>
            </div>
            
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-surface-50 transition-colors"
                  >
                    <h3 className="font-heading font-medium text-lg text-text-primary">
                      {item.question}
                    </h3>
                    <Icon 
                      name={expandedFAQ === index ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      className="text-text-secondary" 
                    />
                  </button>
                  
                  {expandedFAQ === index && (
                    <div className="px-6 pb-4">
                      <p className="text-text-secondary leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emergency Support */}
        <section className="py-16 bg-gradient-to-r from-error to-error-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-xl shadow-strong p-8">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="font-heading font-bold text-2xl text-text-primary mb-4">
                Emergency Support
              </h2>
              <p className="text-text-secondary mb-6">
                For urgent issues during your wedding day or active bookings, our emergency support team is available 24/7.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  iconName="Phone"
                  onClick={() => window.open('tel:+15551234567')}
                >
                  Emergency Hotline
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="MessageCircle"
                  onClick={() => window.open('https://wa.me/15551234567')}
                >
                  WhatsApp Support
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ContactUs;